import * as d3 from "d3";
import $ from "jquery";
import {
  deepCopy,
  genericXHRErrorHandler,
  getAuthHeader,
  getAuthHeaderSync,
  toastError,
  toastErrorSingle,
  toastInfo
} from "src/components/Planner/controller";
import {
  isCategoryLocked
} from "src/components/Planner/planner/categoryDisplayHandler";
import {
  addHeightTextToSerial,
  redrawDevices,
  redrawSerial,
  removeDevice,
  rotateHeightTextForSerial
} from "src/components/Planner/planner/deviceDrawing";
import {
  refreshFloorView
} from "src/components/Planner/planner/plannerCacheInvalidation";
import {
  DeleteSingleDeviceAction
} from "src/components/Planner/planner/history/device-actions";
import {
  getMeasurementsOfSerial,
  removeSerialFromMeasurements
} from "src/components/Planner/planner/measurement";
import {
  d3DevRotatedRectPolygon,
  getXYP,
  isDeviceInIntendedRoom,
  isPlannerMetric,
  placeFree,
  sendDevRecursive,
  snappedPtToPosData,
  unHighlight
} from "src/components/Planner/planner/planner";
import {PlannerGlobals} from "src/components/Planner/planner/planner-globals";
import {
  getDColsNum,
  getDRowsNum,
  SERIAL_SPACING
} from "src/components/Planner/planner/serial-layout";
import {
  canSerializeProdWithSerials,
  getSerialBBox_NoDOM,
  getSerialConnectedPair,
  getSerialConnectionForSerialId,
  getSerialConnectionProps,
  isSerialConnectedWithOther,
  serialRotatedRectPolygon,
  serialsConnectable
} from "src/components/Planner/planner/serialConnections";
import {
  GET_SER_BORDER_WIDTH,
  GET_SER_MARG,
  getD3DevTopCenter,
  getDeviceById,
  getFrameHeight,
  getFrameWidth,
  getGroupsOfDevice,
  getNote,
  getRoomById,
  getSerialById,
  getSerialCenterPos2,
  getSerialOfDeviceId,
  getSerialPosProps,
  getSerObj,
  getSuitablePlaceNearby,
  isPointSnapped,
  rebuildWallsCache,
  serialRealLength,
  uDev,
  UDevGlobals
} from "src/components/Planner/planner/uDev";
import {
  canBeDevMerged,
  getProductOfDevice,
  setAndSendSerialPosition
} from "src/components/Planner/planner/uDev-dom";
import {
  doesDeviceFitRoom,
  rotatePointAroundAnotherXY,
  snapDeviceToWall,
  uMath,
  XYtoNumPoly
} from "src/components/Planner/planner/uMath";
import {ROOT_URI} from "src/utils/constants";
import {inchToCm} from "src/utils/imperialConverter";
import {ts} from "src/utils/translate";

/**
 *
 * @param {Serial} serial
 */
export function ajaxSetSerialProps(serial, includeDevIds = false) {
  return new Promise((res, rej) => {

    const data = {
      serial_id: serial.id,
      height: serial.height,
      direction: serial.direction,
    };

    if (includeDevIds) {
      data.device_ids = serial.deviceIds;
    }


    $.ajax({
      url: ROOT_URI + '/device/update-serial',
      method: 'PUT',
      headers: getAuthHeaderSync(),
      data: data,
      success: () => {
        return res();
      },
      error: (resp) => {
        genericXHRErrorHandler(resp);
        return rej();
      }
    });

  });
}

export function ajaxConnectSerials(srcSerId, tarSerId) {
  return new Promise((res, rej) => {
    $.ajax({
      url: ROOT_URI + '/device/connect-serial',
      method: 'POST',
      headers: getAuthHeaderSync(),
      data: {
        source_serial_id: srcSerId,
        target_serial_id: tarSerId,
      },
      success: (resp) => {
        const newId = resp.id;

        const conn = {
          id: newId,
          serialIds: [srcSerId, tarSerId]
        };

        return res(conn);
      },
      error: (resp) => {
        genericXHRErrorHandler(resp);
        return rej();
      }
    });
  });
}

export function connectSerials(srcSerId, tarSerId) {
  return new Promise((res, rej) => {
    const srcSer = getSerialById(srcSerId);
    const tarSer = getSerialById(tarSerId);
    if (!srcSer || !tarSer) { return rej(false); }

    /* if (Math.abs(srcSer.height - tarSer.height) < 12) {
        toastErrorSingle('These frames are too close to each other. Set their height so they differ by more than 12 cm.', false, 'con-ser-err');
        return rej(false);
    } */

    const srcInConn = isSerialConnectedWithOther(srcSerId);
    const tarInConn = isSerialConnectedWithOther(tarSerId);

    if (srcInConn || tarInConn) {
      toastErrorSingle('Only two frames can be paired with each other', false, 'con-ser-err');
      return rej(false);
    }
    else {
      const oriTopMid = getD3DevTopCenter(getSerObj(tarSer));

      ajaxConnectSerials(srcSerId, tarSerId).then(conn => {
        PlannerGlobals.serialConnections.push(conn);

        const srcFirstDev = getDeviceById(srcSer.deviceIds[0]);
        const tarFirstDev = getDeviceById(tarSer.deviceIds[0]);

        const prePos = {
          srcSer: {
            x: srcFirstDev.origiposition ? srcFirstDev.origiposition[0] : srcFirstDev.x,
            y: srcFirstDev.origiposition ? srcFirstDev.origiposition[1] : srcFirstDev.y,
            angle: srcFirstDev.origiAngle || srcFirstDev.angle,
            roomId: srcFirstDev.origiRoomId || srcFirstDev.roomId,
            height: srcSer.height,
          },
          tarSer: {
            x: tarFirstDev.x,
            y: tarFirstDev.y,
            angle: tarFirstDev.angle,
            height: tarSer.height,
            roomId: tarFirstDev.roomId
          },
        };

        const proms = [];

        const props = getSerialConnectionProps(conn.id);
        const topMidSnapped = isPointSnapped(oriTopMid);
        const snappedPos = topMidSnapped && snapDeviceToWall(oriTopMid, PlannerGlobals.DEVICE_SIZE * 10, props.allColsNum, false, undefined, undefined, undefined, undefined, props.allRowsNum);
        if (topMidSnapped && snappedPos) {
          const posProps = snappedPtToPosData(snappedPos);
          proms.push(setAndSendSerialPosition(srcSerId, posProps));
          proms.push(setAndSendSerialPosition(tarSerId, posProps));
        }
        else {
          const isGood = isDeviceInIntendedRoom(props);
          if (!isGood) {
            const devRoom = getRoomById(tarFirstDev.roomId);
            rebuildWallsCache(devRoom, false, false);
            const snappedPlace = snapDeviceToWall(props, PlannerGlobals.DEVICE_SIZE * 10, props.allColsNum, undefined, undefined, undefined, undefined, undefined, props.allRowsNum);
            console.log(snappedPlace)
            if (snappedPlace) {
              const pos = { x: snappedPlace.point.x, y: snappedPlace.point.y, roomId: snappedPlace.room.id, angle: snappedPlace.rotation };
              props.serials.forEach(ser => {
                proms.push(setAndSendSerialPosition(ser.id, pos));
              });
            }
          }
          else {
            setAndSendSerialPosition(srcSer.id, tarFirstDev);
          }
        }

        redrawDevices();
        unHighlight();

        Promise.all(proms)
        .then(() => {
          return res({ newId: conn.id, prePos });
        })
        .catch(rej);
      })
      .catch(() => rej());
    }
  });
}

export function ajaxDetachConnection(connId) {
  return new Promise((res, rej) => {
    $.ajax({
      url: ROOT_URI + '/device/delete-serial-connection',
      method: 'DELETE',
      headers: getAuthHeaderSync(),
      data: {
        serial_connection_id: connId,
      },
      success: (resp) => {
        return res();
      },
      error: (resp) => {
        genericXHRErrorHandler(resp);
      }
    });
  });
}

export function detachSerialConnection(connId) {
  return new Promise((res, rej) => {

    const connProps = getSerialConnectionProps(connId);
    if (connProps) {
      ajaxDetachConnection(connId)
      .then(() => {
        const proms = [];

        connProps.serials.forEach((ser, idx) => {
          const angle = connProps.angle;
          const realSer = getSerialById(ser.serialId);
          const firstDevCenter = uDev.getCenterOfDevice_NoDOM(realSer.deviceIds[0]);
          const serCenterPt = getSerialCenterPos2(realSer);

          const firstDevCenterPtOri = rotatePointAroundAnotherXY(firstDevCenter, serCenterPt, -angle);
          const x = firstDevCenterPtOri.x - PlannerGlobals.DEVICE_SIZE / 2;
          const y = firstDevCenterPtOri.y - PlannerGlobals.DEVICE_SIZE / 2;

          proms.push(setAndSendSerialPosition(realSer.id, { x, y, angle }));
        });

        PlannerGlobals.serialConnections = PlannerGlobals.serialConnections.filter(conn => conn.id != connId);
        //FAKE_removeSerConnProps(connId);
        redrawDevices();
        unHighlight();

        return Promise.all(proms).then(res).catch(rej);
      })
      .catch(() => rej());
    }
    else {
      return rej();
    }
  });
}

export function getSerialPropsInConnection(serialId) {
  const conn = getSerialConnectionForSerialId(serialId);
  if (conn) {
    const props = getSerialConnectionProps(conn.id);
    return props.serials.find(ser => ser.serialId == serialId);
  }
  else {
    return null;
  }
}

/**
 * Removing a serial by its ID
 *
 * @param {any} sid The serial ID
 */
export function removeSerialLocally(sid, oriTopMid = null,
    deletedByWizard = false, allowSerialDisplacement = false) {
  var idx = PlannerGlobals.serials.findIndex(function (serial) {
    return serial.id == sid;
  });
  if (idx != -1) {
    const conn = getSerialConnectionForSerialId(sid);
    if (conn) {
      const connectedPair = getSerialConnectedPair(sid);
      if (connectedPair && oriTopMid && isPointSnapped(oriTopMid)
          && (!deletedByWizard || allowSerialDisplacement)) {
        const connSerObj = getSerObj(connectedPair);
        const cols = getDColsNum(connSerObj);
        const rows = getDRowsNum(connSerObj);
        const snappedPlace = snapDeviceToWall(oriTopMid,
            PlannerGlobals.DEVICE_SIZE * 10, cols, false, undefined, undefined,
            undefined, undefined, rows);
        if (snappedPlace) {
          setAndSendSerialPosition(connectedPair.id,
              snappedPtToPosData(snappedPlace));
        }
      }

      PlannerGlobals.serialConnections = PlannerGlobals.serialConnections.filter(
          x => x.id != conn.id);
    }

    removeSerialFromMeasurements(sid);
    PlannerGlobals.serials.splice(idx, 1);
    delete UDevGlobals.serialIdMap[sid];
  }
}

/**
 * Removing a device from its serial (if it has one), also removing the serial if it isn't needed anymore
 *
 * @param {any} deviceId The device's ID
 * @returns {boolean} did the serial get completely deleted
 */
export function removeDeviceFromSerial(deviceId, allDel = false,
    deletedByWizard = false, allowSerialDisplacement = false) {
  var devSer = getSerialOfDeviceId(deviceId);
  if (devSer) {
    const conn = getSerialConnectionForSerialId(devSer.id);
    const connectedPair = conn && getSerialConnectedPair(devSer.id);
    const origiD3Dev = conn ? getSerialConnectionProps(conn.id) : getSerObj(
        devSer);
    const oriTopMid = getD3DevTopCenter(origiD3Dev);
    const oriConnPairHorizLen = connectedPair ? (connectedPair.direction
    == "horizontal" ? serialRealLength(connectedPair) : 1) : 0;
    const oriDevSerDist = serialRealLength(devSer);

    if (UDevGlobals.devIdSerialCache[deviceId]) {
      delete UDevGlobals.devIdSerialCache[deviceId];
    }
    var delDevIdx = devSer.deviceIds.findIndex(function (id) {
      return id == deviceId;
    });
    if (delDevIdx === -1) {
      return false;
    }

    devSer.deviceIds.splice(delDevIdx, 1);

    if (allDel || devSer.deviceIds.length === 0) {
      removeSerialLocally(devSer.id, oriTopMid, deletedByWizard,
          allowSerialDisplacement);
      return true;
    }

    var deletedDev = getDeviceById(deviceId);
    var firstSerialDev = getDeviceById(devSer.deviceIds[0]);
    firstSerialDev.x = origiD3Dev.x;
    firstSerialDev.y = origiD3Dev.y;

    // push serial to the right so its center stays the same
    if (deletedDev && deletedDev.qty < 2 && (!deletedByWizard
        || allowSerialDisplacement)) {
      if (!connectedPair || oriConnPairHorizLen < oriDevSerDist
          || devSer.direction === "vertical") {
        if (devSer.direction === "horizontal") {
          firstSerialDev.x += PlannerGlobals.DEVICE_SIZE / 2 + SERIAL_SPACING
              / 2;
        } else {
          if (isPointSnapped(oriTopMid)) {
            const newD3Obj = conn ? getSerialConnectionProps(conn.id)
                : getSerObj(devSer);
            const rows = getDRowsNum(newD3Obj);
            const cols = getDColsNum(newD3Obj);

            const snappedPt = snapDeviceToWall(oriTopMid,
                PlannerGlobals.DEVICE_SIZE * 10, cols, false, undefined,
                undefined, undefined, undefined, rows);
            if (snappedPt) {
              firstSerialDev.x = snappedPt.point.x;
              firstSerialDev.y = snappedPt.point.y;
              firstSerialDev.angle = snappedPt.rotation;
              firstSerialDev.roomId = snappedPt.room.id;
            }
          } else {
            firstSerialDev.y += PlannerGlobals.DEVICE_SIZE / 2 + SERIAL_SPACING
                / 2;
          }
        }

        setAndSendSerialPosition(devSer.id, firstSerialDev);
        if (connectedPair) {
          setAndSendSerialPosition(connectedPair.id, firstSerialDev);
        }
      }
    }
  }
}

/**
 *
 * @param {number} x
 * @param {number} y
 * @param {number} roomId
 * @param {*} products
 * @param {number} angle
 * @returns {JQuery.Deferred<any,any,any>}
 */
export function placeSerialDevices(x, y, roomId, products, angle = 0,
    ignorePlaceFree = false, height = undefined, direction = undefined) {
  var mainDef = $.Deferred();

  if (!ignorePlaceFree && !placeFree(x, y)) {
    mainDef.reject();
    return mainDef;
  }

  unHighlight();
  var room = getRoomById(roomId);
  /* var roomPoly = XYtoNumPoly(room.poly); */
  var diffDevs = {}
  var ddc = 0;
  for (var i in products) {
    var prod = products[i];
    var pnid = +prod.id.replace('product_', '');
    if (!(prod.id in diffDevs) || !canBeDevMerged(prod)) {
      diffDevs[prod.id] = true;
      ddc++;
    }
  }

  if (!doesDeviceFitRoom(roomId, {x: x, y: y}, angle, ddc)) {
    toastInfo(ts('p_serial_in_room_please', "The frame must fit in the room."));
    mainDef.reject();
    return mainDef;
  }

  var fakeSerial = {
    id: PlannerGlobals.curr_fake_serid--,
    deviceIds: [],
    angle: angle,
    roomId: roomId,
    height: height || getSerialHeightForPNID(
        products[0].id.replace('product_', '')),
    direction: direction || 'horizontal',
  };
  fakeSerial.initialFakeSerialId = fakeSerial.id;
  PlannerGlobals.serials.push(fakeSerial);
  var defs = [];

  var prods = [];

  for (var i = 0; i < products.length; i++) {
    var prod = products[i];
    defs.push($.Deferred());
    var id = prod.id.replace("product_", "");

    var newdev = {
      x: x,
      y: y,
      roomId: +roomId,
      deviceId: PlannerGlobals.curr_fake_devid--,
      image: prod.image,
      input: prod.input,
      serializable: prod.serializable,
      max_serializable: prod.max_serializable,
      groupable: prod.groupable,
      category_id: +prod.parent.replace('category_', ''),
      product_name_id: +id,
      ghost: true,
      nopermission: false,
      is_loxone: prod.is_loxone,
      has_design: prod.has_design,
      is_nhc: prod.is_nhc,
      product_id: prod.product_id,
      image_url: prod.image_url,
      device_manufacturer: prod.device_manufacturer,
      mergeable: prod.mergeable,
      device_color: prod.device_color,
      angle: angle,
      note_message: prod.note_message || null,
      prevent_note: prod.prevent_note || false
    };
    newdev.initialFakeDeviceId = newdev.deviceId;
    PlannerGlobals.devices.push(newdev);
    prods.push(newdev);
    fakeSerial.deviceIds.push(newdev.deviceId);

  }
  sendDevRecursive(fakeSerial, defs, prods, 0);
  requestAnimationFrame(function () {
    redrawSerial(fakeSerial.id);
    /* redrawDevices(); */
    $.when.apply($, defs)
    .done(function () {
      var bads = prods.filter(function (prod) {
        return prod.errored;
      });
      prods = prods.filter(function (prod) {
        return !prod.errored;
      });
      bads.forEach(function (bad) {
        PlannerGlobals.devices = PlannerGlobals.devices.filter(function (dev) {
          return dev.deviceId !== bad.deviceId;
        });
        fakeSerial.deviceIds = fakeSerial.deviceIds.filter(function (id) {
          return id !== bad.deviceId;
        });
      });
      if (bads.length === prods.length) {
        removeSerialLocally(fakeSerial);
      }
      if (bads.length) {
        redrawSerial(fakeSerial.id);
      }

      prods.slice(1).reduce((prevProm, prod) => {
        return prevProm.then(() => addDevToSerial(prod.deviceId, fakeSerial));
      }, Promise.resolve())
      .then(() => mainDef.resolve(fakeSerial))
      .catch(() => mainDef.reject());
    })
    .fail(function () {
      mainDef.reject();
    });
  });

  mainDef.done(function () {
    refreshFloorView();
  });
  return mainDef;
}

/**
 *
 * @param {*} serialId
 * @returns {JQuery.Deferred<any,any,any>}
 */
export function removeSerialDevices(serialId) {
  var mainDef = $.Deferred();
  var ser = getSerialById(serialId);
  var removedDevParams = [];
  if (ser) {
    var deferreds = [];
    var measures = getMeasurementsOfSerial(serialId);
    var serConn = getSerialConnectionForSerialId(serialId);
    for (var i = ser.deviceIds.length - 1; i >= 0; i--) {
      let devDeferred = $.Deferred();
      deferreds.push(devDeferred);
      let idx = i;
      var dev = getDeviceById(ser.deviceIds[i]);
      if (dev) {
        removeDevice(null, dev.deviceId)
        .done(function (params) {
          removedDevParams[idx] = params;
          devDeferred.resolve();
        })
        .fail(function () {
          devDeferred.reject();
        });
      } else {
        devDeferred.resolve();
      }
    }
    $.when.apply($, deferreds)
    .done(function () {
      mainDef.resolve(removedDevParams, measures, serConn);
    })
    .fail(function () {
      mainDef.reject();
    });
  } else {
    mainDef.resolve([], [], null);
  }

  mainDef.done(function () {
    refreshFloorView();
  });

  return mainDef;
}

/**
 *
 * @param {Serial} serial
 * @param {Device} device
 */
export function serialAlreadyContainsBadgedDevType(serial, device) {
  return canBeDevMerged(device) && serial.deviceIds
  .map(getDeviceById)
  .filter(function (dev) {
    return dev.deviceId != device.deviceId && dev.product_name_id
        == device.product_name_id;
  })
      .length > 0;
}

export function addNewDesignIdToDefaultSerial(productId, deviceId) {
  getAuthHeader(function (header) {
    $.ajax({
      headers: header,
      url: ROOT_URI + '/device/set-new-design-to-device',
      type: 'POST',
      dataType: 'JSON',
      async: false,
      data: {
        productId: productId,
        deviceId: deviceId
      },
      success: function (response) {
        for (let i = 0; i < PlannerGlobals.devices.length; i++) {
          if (PlannerGlobals.devices[i].deviceId == deviceId) {
            PlannerGlobals.devices[i].image_url = response.image_url;
            PlannerGlobals.devices[i].device_manufacturer = response.manufacturer;
            PlannerGlobals.devices[i].device_color = response.color;
            PlannerGlobals.devices[i].product_id = response.product_id;
          }
        }

      }
    });
  });
}

export function detachFromSerial(deviceId, newSerial = null) {
  return new Promise((res, rej) => {
    const dev = getDeviceById(deviceId);
    const prod = getProductOfDevice(dev);
    const ser = getSerialOfDeviceId(deviceId);
    if (dev && prod && ser) {
      const devPlace = uDev.getCenterOfDevice_NoDOM(deviceId);
      if (devPlace) {

        let placePt = {x: dev.x, y: dev.y};
        let placeRoomId = dev.roomId;
        let placeAngle = dev.angle;

        const suitablePlace = getSuitablePlaceNearby(devPlace, ser.angle,
            PlannerGlobals.DEVICE_SIZE / 2, 4, 2);
        if (suitablePlace) {
          placePt = suitablePlace.point;
          placeRoomId = suitablePlace.room.id;
          placeAngle = suitablePlace.rotation;
        }

        const copyDev = deepCopy(dev);

        copyDev.x = placePt.x;
        copyDev.y = placePt.y;
        copyDev.angle = placeAngle;
        copyDev.roomId = placeRoomId;

        //const action = new CreateSingleDeviceAction(dev, true, false);
        const action = new DeleteSingleDeviceAction({
          device: copyDev,
          groups: getGroupsOfDevice(deviceId),
          note: copyDev.noteId ? getNote(deviceId, "dev") : null,
          removedMeasures: [],
          serial: newSerial,
          wasLastOfSerial: !newSerial,
          preventSerial: !newSerial
        });

        removeDevice(null, deviceId, true, true)
        .then(() => {
          action.undo(result => {
            if (result) {
              redrawDevices();
              return res(action.device.deviceId);
            } else {
              return rej(false);
            }
          })
        })
        .catch(() => {
          return rej(false);
        });
      }
    } else {
      return rej(false);
    }
  });
}

/**
 * @deprecated Use d3DevRotatedRectPolygon instead
 * @param {D3Device} d3Dev
 * @returns {[number,number][]}
 */
export function getRotatedSerialCorners(d3Dev) {
  return XYtoNumPoly(d3DevRotatedRectPolygon(d3Dev));
}

export function getSerialD(serialId) {
  const ser = getSerialById(serialId);
  if (ser) {
    return getSerObj(ser);
  }

  return null;
}

export function serialOnClick(e) {
  var serialId = d3.select(this).attr('data-serialId');
  if (serialId) {
    unHighlight();
    highlightSerial(serialId);
  }
}

export function redrawSerializerBorders() {
  PlannerGlobals.svg.selectAll('.serialize-frame').remove();

  const serMarg = GET_SER_MARG();

  PlannerGlobals.svg.selectAll('.serial')
  .each(function (d) {
    if (!d.devices.length) {
      return;
    }
    //var childrenLength = d.devices.length;
    const posProps = getSerialPosProps(d.devices.length, d.direction);

    d3.select(this).insert('rect', ':first-child')
    .attr('x', -serMarg)
    .attr('y', -serMarg)
    .attr('rx', 2 * PlannerGlobals.DEVICE_SIZE / 20)
    .attr('ry', 2 * PlannerGlobals.DEVICE_SIZE / 20)
    .attr('fill', 'rgba(255,255,255,0.3)')
    .attr('width', posProps.width)
    .attr('height', posProps.height)
    .attr('stroke', 'black')
    .attr('stroke-width', GET_SER_BORDER_WIDTH())
    .attr('data-serialIdRect', d.id);
  });

  PlannerGlobals.svg.selectAll('.serial-connected')
  .each(function (d) {
    if (!d.devices.length) {
      return;
    }

    const dir = this.getAttribute('data-serialDir');
    const devNum = +this.getAttribute('data-serialDevNum');
    const hiddenDevNum = +this.getAttribute('data-serialHiddenDevs');

    if (devNum == hiddenDevNum) {
      return;
    }

    const id = this.getAttribute('data-serialId');
    let width = getFrameWidth(+devNum);
    let height = getFrameHeight();
    if (dir === "vertical") {
      let temp = width;
      width = height;
      height = temp;
    }
    d3.select(this).insert('rect', ':first-child')
      .attr('x', -serMarg)
      .attr('y', -serMarg)
      .attr('rx', 2 * PlannerGlobals.DEVICE_SIZE / 20)
      .attr('ry', 2 * PlannerGlobals.DEVICE_SIZE / 20)
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'rgba(255,255,255,0.3)')
      .attr('stroke', 'black')
      .attr('stroke-width', GET_SER_BORDER_WIDTH())
      .attr('data-serialIdRect', id);
  });

  PlannerGlobals.svg.selectAll('.serial, .serial-connected').each(function (d) {
    if (this.hasAttribute('data-serialDevNum') && this.hasAttribute(
        'data-serialHiddenDevs')) {
      const devNum = +this.getAttribute('data-serialDevNum');
      const hiddenDevNum = +this.getAttribute('data-serialHiddenDevs');

      if (devNum == hiddenDevNum) {
        return;
      }
    } else if (!d.devices.length) {
      return;
    }

    const id = this.getAttribute('data-serialId');
    const ser = getSerialById(id);
    const height = ser.height;
    let heightTxtY = PlannerGlobals.DEVICE_SIZE - (PlannerGlobals.DEVICE_SIZE
        / 3);

    if (ser.direction === "vertical") {
      const connectedPair = getSerialConnectedPair(id);
      if (connectedPair && connectedPair.height > height) {
        heightTxtY += (serialRealLength(ser) - 1) * (PlannerGlobals.DEVICE_SIZE
            + SERIAL_SPACING);
      }
    }

    const textSelection = addHeightTextToSerial(d3.select(this), ser,
        heightTxtY);
    rotateHeightTextForSerial(ser, textSelection);
  });
}

/**
 *
 * @param {Product} prod
 */
export function highlightAvailableSerials(prod) {
  if (prod.serializable) {
    const prodHeight = getSerialHeightForPNID(prod.id.replace('product_', ''));
    const minDistCM = isPlannerMetric() ? 12 : inchToCm(5);

    PlannerGlobals.serials.forEach(serial => {

      if ((serial.height == prodHeight || Math.abs(serial.height - prodHeight)
              < minDistCM) && serial.deviceIds.length < 5
          && canSerializeProdWithSerials(prod, serial)) {
        highlightSerial(serial.id, 'rgb(0, 220, 44)', 'rgba(2, 86, 0, 0.98)',
            true);
        $('#devicesMain').find(`[data-serialIdRect="${serial.id}"]`).addClass(
            'serial-mrg-hover-highlight');
      } else {
        const conn = getSerialConnectionForSerialId(serial.id);
        if (!conn && Math.abs(serial.height - prodHeight) >= minDistCM) {
          highlightSerial(serial.id, '#2196f36e', '#2196f3ad', true);
          $('#devicesMain').find(`[data-serialIdRect="${serial.id}"]`).addClass(
              'serial-conn-hover-highlight');
        } else if (conn && serial.deviceIds.length < 5) {
          const connPair = getSerialConnectedPair(serial.id);
          if (connPair && connPair.height != prodHeight) {
            highlightSerial(serial.id, 'rgb(0, 220, 44)',
                'rgba(2, 86, 0, 0.98)', true);
            $('#devicesMain').find(`[data-serialIdRect="${serial.id}"]`).addClass(
                'serial-mrg-hover-highlight');
          }
        }
      }
    });
  }
}

export function highlightSerial(serialId, fillColor, strokeColor, dontClipboard,
    actualClickedDevId) {
  if (!fillColor) {
    fillColor = 'rgba(0, 220, 165, 0.1)';
  }

  if (!strokeColor) {
    strokeColor = 'rgba(0, 183, 137, 0.87)';
  }
  var highlightedSerial = getSerialById(serialId);
  if (!highlightedSerial) {
    return;
  }
  for (var i = 0; i < highlightedSerial.deviceIds.length; i++) {
    var dev = getDeviceById(highlightedSerial.deviceIds[i]);
    if (!dev || isCategoryLocked(dev.category_id)) {
      return;
    }
  }

  if (!actualClickedDevId) {
    actualClickedDevId = highlightedSerial.deviceIds[0];
  }

  var bbox = getSerialBBox_NoDOM(serialId);

  PlannerGlobals.container.insert('g', '.devices').attr('class', 'highlights')
  .insert('rect')
  .attr('class', 'highlight')
  .attr('x', bbox.x)
  .attr('y', bbox.y)
  .attr('width', bbox.width)
  .attr('height', bbox.height)
  .attr('transform', bbox.transform)
  .attr('fill', fillColor)
  .attr('stroke', strokeColor)
  .attr('stroke-width', GET_SER_BORDER_WIDTH() * 1.7)
  .attr('rx', '1.2px')
  .attr('ry', '1.2px');

  if (!dontClipboard) {
    if (highlightedSerial.deviceIds.length === 1) {
      PlannerGlobals.currentlySelectedElement = {
        type: 'device',
        content: getDeviceById(highlightedSerial.deviceIds[0])
      };
    } else {
      PlannerGlobals.currentlySelectedElement = {
        type: 'serial',
        content: highlightedSerial,
        actualClickedDevId
      };
    }
  }

  return bbox;
}

/**
 *
 * @param {D3Data} d
 * @returns {Serial[]}
 */
export function getOverlappingSerials(d, invalidsToo = false) {
  if (PlannerGlobals.sizeErrorHelper.errorCorrectMode) {
    return [];
  }
  if (d.id) {
    const realSer = getSerialById(d.id);
    const draggedPoly = serialRotatedRectPolygon(d.id, 0.15);
    const cursorPt = getXYP();

    const compatibles = PlannerGlobals.serials
    .filter(ser => ser.id != d.id && (invalidsToo || ser.deviceIds.length
        + realSer.deviceIds.length <= 5 || serialsConnectable(d.id, ser.id)))
    .map(ser => {
      const serPoly = serialRotatedRectPolygon(ser.id, 0.15);
      if (uMath.polygonsIntersect(draggedPoly, serPoly) || uMath.polygonsInLine(
          draggedPoly, serPoly)) {
        const cursorRectDist = uMath.pointPolyDistance(cursorPt, serPoly);

        return {ser, cursorRectDist};
      } else {
        return null;
      }
    })
    .filter(ser => {
      return !!ser;
    })
    .sort((a, b) => a.cursorRectDist - b.cursorRectDist)
    .map(x => x.ser);

    return compatibles;
  } else if (d.connId && invalidsToo) {
    /* const draggedPoly = d3DevRotatedRectPolygon(d, 0.01); */
    const draggedPolies = d.serials.map(
        x => serialRotatedRectPolygon(x.id, 0.15));
    const cursorPt = getXYP();

    const overlaps = PlannerGlobals.serials
    .filter(ser => !d.serials.find(x => x.id == ser.id))
    .map(ser => {
      const serPoly = serialRotatedRectPolygon(ser.id, 0.15);
      if (draggedPolies.some(poly => uMath.polygonsIntersect(poly, serPoly)
          || uMath.polygonsInLine(poly, serPoly))) {
        const cursorRectDist = uMath.pointPolyDistance(cursorPt, serPoly);

        return {ser, cursorRectDist};
      } else {
        return null;
      }
    })
    .filter(ser => {
      return !!ser;
    })
    .sort((a, b) => a.cursorRectDist - b.cursorRectDist)
    .map(x => x.ser);

    return overlaps;
  }
  return [];
}

/**
 *
 * @param {Serial[]} serials
 * @param {D3Device} d3Serial
 */
export function shadowOverlappingSerials(serials, d3Serial) {
  if (!d3Serial.id) {
    return;
  }
  const ser = getSerialById(d3Serial.id);
  if (!ser) {
    return;
  }
  $('#devicesMain')
  .find(
      '.serial-overlap-shadow-both, .serial-overlap-shadow-mrg, .serial-overlap-shadow-conn')
  .removeClass(
      'serial-overlap-shadow-both serial-overlap-shadow-mrg serial-overlap-shadow-conn');

  const currDevNum = ser.deviceIds.length;

  serials.forEach(otherSerial => {
    let className = '';

    const mergeable = otherSerial.deviceIds.length + currDevNum <= 4;
    const connectable = serialsConnectable(d3Serial.id, otherSerial.id);
    if (mergeable && connectable) {
      className = 'serial-overlap-shadow-both';
    } else if (mergeable) {
      className = 'serial-overlap-shadow-mrg';
    } else if (connectable) {
      className = 'serial-overlap-shadow-conn';
    }

    $('#devicesMain').find(`[data-serialIdRect="${otherSerial.id}"]`).addClass(
        className);
  });
}

/**
 * Returns the default height for a serialized device.
 * It always returns a CM value, but it takes into account when the planner is in imperial mode.
 * In imperial mode, the return value is a CM value that was converted from inch.
 * @param {number} pnid
 * @returns number
 */
export function getSerialHeightForPNID(pnid) {
  const lowerNum = isPlannerMetric() ? 30 : inchToCm(12);
  const higherNum = isPlannerMetric() ? 140 : inchToCm(55);

  const heightForPnid = {
    1: lowerNum,
    2: lowerNum,
    3: lowerNum,
    4: lowerNum,
    5: lowerNum,
    6: lowerNum,
    7: lowerNum,
    8: lowerNum,
    9: lowerNum,
    10: lowerNum,
    11: lowerNum,
    12: lowerNum,
    133: lowerNum,
    287: lowerNum,
    288: lowerNum,
    289: lowerNum,
    290: lowerNum,
    306: lowerNum,
    321: lowerNum,
    484: lowerNum,
  }

  return heightForPnid[pnid] || higherNum;
}

/**
 *
 * @param {number} newDevId
 * @param {Serial} serialOfTargetDev
 */
export function addDevToSerial(newDevId, serialOfTargetDev, callback) {
  return new Promise((res, rej) => {
    if (serialOfTargetDev.id < 0) {
      return rej();
    }

    var targetDevId = serialOfTargetDev.deviceIds[0];
    // if (serialOfTargetDev.deviceIds.length < 4 && getDeviceById(newDevId).serializable) {
    getAuthHeader(function (header) {
      $.ajax({
        headers: header,
        url: ROOT_URI + '/device/serial-device',
        dataType: 'JSON',
        type: 'POST',
        data: {
          target_device_id: targetDevId,
          source_device_id: newDevId
        },
        success: function (resp) {
          if (callback) {
            callback(true);
          }
          return res();
        },
        error: function (response) {
          if (callback) {
            callback(true);
          }
          toastError(response.responseJSON.message);
          return res();
          if (response.responseJSON && response.responseJSON.message) {
            console.log('something happened while serializing',
                response.responseJSON.message);
          }
        }
      });
    });
    //}
  });
}

export function mergeSerials(srcSerId, tarSerId) {
  return new Promise((res, rej) => {

    const srcSer = getSerialById(srcSerId);
    const tarSer = getSerialById(tarSerId);
    if (srcSer && tarSer) {
      d3.select(`[data-serialId="${srcSerId}"]`).remove();

      const actions = srcSer.deviceIds.map(getDeviceById)
      .map(dev => {
        const action = new DeleteSingleDeviceAction({
          device: dev,
          groups: getGroupsOfDevice(dev.deviceId),
          note: dev.noteId ? getNote(dev.deviceId, "dev") : null,
          removedMeasures: [],
          serial: tarSer,
          wasLastOfSerial: false
        });

        action.serialIdChanged = () => { };

        return action;
      });

      removeSerialDevices(srcSer.id)
      .then(() => {
        actions.reduce((p, action) => {
          return p.finally(() => new Promise((innerRes, innerRej) => {
            action.undo(result => {
              if (result) {
                return innerRes(result);
              }
              else {
                return innerRej(result);
              }
            });
          }));
        }, Promise.resolve())
        .then(() => {
          const movedDevNewIds = actions.map(a => a.device.deviceId);
          return res(movedDevNewIds);
        })
        .catch(() => {
          return rej(false);
        })
      })
      .catch(() => {
        redrawDevices();
        return rej(false);
      });
    }
  });
}


